<script lang="ts" setup>
import { computed, onBeforeMount } from 'vue';
import { useI18n } from 'vue-i18n';

import { accountService } from '@exchange/libs/account/service/src';
import { authService } from '@exchange/libs/auth/service/src';
import { futuresService, marginService } from '@exchange/libs/futures/service/src';
import { ACCOUNT_RATIO_STATUS_THRESHOLD_LABELS, AccountRatioStatus } from '@exchange/libs/futures/service/src/lib/account-ratio';
import { useFutures } from '@exchange/libs/futures/ui/src/lib/useFutures';
import { subaccountsService } from '@exchange/libs/trading-accounts/service/src';

const { t } = useI18n({ useScope: 'global' });

const { accountId } = accountService;
const { accountRatio, accountRatioStatus, balance: marginBalance, equity, maintenanceMargin } = marginService;
const { getAccountById } = subaccountsService;
const { getFuturesPositions: futuresPositions } = useFutures();

const account = computed(() => getAccountById(accountId.value));
const isAuthenticated = computed(() => authService.hasBeenAuthenticated);

// Computed values from futures positions
const accountData = computed(() => {
  const accountDataDefaults = {
    marginRatio: '-',
    marginRatioStatus: null,
    maintenanceMargin: maintenanceMargin.value,
    leverage: '-',
    positionValue: 0,
    equity: equity.value,
    availableMargin: marginBalance.value.available,
    lockedMargin: marginBalance.value.used,
    realisedPnL: 0,
  };

  if (!isAuthenticated.value || !account.value || !futuresPositions.value.length) {
    return accountDataDefaults;
  }

  const totalPositionValue = futuresPositions.value.reduce((sum, pos) => sum + Number(pos.open_position), 0);
  const totalRealisedPnL = futuresPositions.value.reduce((sum, pos) => sum + Number(pos.realised_pnl ?? 0), 0);
  const totalNotionalValue = futuresPositions.value.reduce((sum, pos) => sum + Math.abs(Number(pos.open_volume) * Number(pos.mark_price ?? 0)), 0);
  const combinedLeverage = equity.value > 0 ? totalNotionalValue / equity.value : 0;

  return {
    ...accountDataDefaults,
    marginRatio: accountRatio.value ? accountRatio.value.toFixed(2) : accountDataDefaults.marginRatio,
    marginRatioStatus: accountRatioStatus.value,
    leverage: `${combinedLeverage.toFixed(2)}x`,
    positionValue: totalPositionValue,
    realisedPnL: totalRealisedPnL,
  };
});

const getTooltip = (d: { message: string; html?: boolean }) => ({
  content: d.message,
  placement: 'top',
  html: d.html,
});

onBeforeMount(futuresService.getLeverage);
</script>

<template>
  <ot-module-container :scroll-settings="{ suppressScrollX: false, suppressScrollY: false }">
    <template #main-content>
      <div class="account-overview">
        <div class="account-overview__container">
          <section class="account-overview__section">
            <h3 class="account-overview__title">
              {{ $t('modules.accountOverview.header.marginLevel') }}
              <span
                v-if="account?.name"
                class="account-overview__subtitle"
                >{{ account?.name }}</span
              >
            </h3>

            <div class="account-overview__row">
              <span class="account-overview__label">
                {{ $t('modules.accountOverview.header.marginRatio') }}
                <ot-icon
                  v-tooltip="
                    getTooltip({
                      message: Object.entries(ACCOUNT_RATIO_STATUS_THRESHOLD_LABELS)
                        .map(
                          ([status, thresholdLabel]) => `
                        <div>
                          <span class='account-overview__margin-ratio--${status.toLowerCase()}'>${thresholdLabel}</span>
                          - ${t(`fundamentals.accountRatio.status.${status}`)}
                        </div>
                      `,
                        )
                        .join(''),
                      html: true,
                    })
                  "
                  class="account-overview__info-icon"
                  name="info-circle"
                />
                <ot-lozenge
                  v-if="accountData.marginRatioStatus === AccountRatioStatus.DERISK || accountData.marginRatioStatus === AccountRatioStatus.LIQUIDATION"
                  class="account-overview__margin-ratio-lozenge"
                  :class="`account-overview__margin-ratio-lozenge--${accountData.marginRatioStatus?.toLowerCase()}`"
                  :filled="false"
                  :text="t(`fundamentals.accountRatio.status.${accountData.marginRatioStatus}`)"
                  uppercase
                />
              </span>
              <span
                class="account-overview__value"
                :class="accountData.marginRatio !== '-' && `account-overview__margin-ratio--${accountData.marginRatioStatus?.toLowerCase()}`"
              >
                {{ accountData.marginRatio }}
              </span>
            </div>

            <div class="account-overview__row">
              <span class="account-overview__label">{{ $t('modules.accountOverview.header.maintenanceMargin') }}</span>
              <span class="account-overview__value">
                <ot-number-display
                  :number="accountData.maintenanceMargin"
                  :precision="2"
                  display-style="currency"
                  currency-display="symbol"
                  :grouping="true"
                />
              </span>
            </div>

            <div class="account-overview__row">
              <span class="account-overview__label">{{ $t('fundamentals.leverage') }}</span>
              <span class="account-overview__value">{{ accountData.leverage }}</span>
            </div>

            <div class="account-overview__row">
              <span class="account-overview__label">{{ $t('modules.accountOverview.header.positionValue') }}</span>
              <span class="account-overview__value">
                <ot-number-display
                  :number="accountData.positionValue"
                  :precision="2"
                  display-style="currency"
                  currency-display="symbol"
                  :grouping="true"
                />
              </span>
            </div>
          </section>

          <section class="account-overview__section">
            <h3 class="account-overview__title">{{ $t('modules.accountOverview.header.assets') }}</h3>

            <div class="account-overview__row">
              <span class="account-overview__label">{{ $t('modules.accountOverview.header.equity') }}</span>
              <span class="account-overview__value">
                <ot-number-display
                  :number="accountData.equity"
                  :precision="2"
                  display-style="currency"
                  currency-display="symbol"
                  :grouping="true"
                />
              </span>
            </div>

            <div class="account-overview__row">
              <span class="account-overview__label">{{ $t('modules.accountOverview.header.availableMargin') }}</span>
              <span class="account-overview__value">
                <ot-number-display
                  :number="accountData.availableMargin"
                  :precision="2"
                  display-style="currency"
                  currency-display="symbol"
                  :grouping="true"
                />
              </span>
            </div>

            <div class="account-overview__row">
              <span class="account-overview__label">{{ $t('modules.accountOverview.header.usedMargin') }}</span>
              <span class="account-overview__value">
                <ot-number-display
                  :number="accountData.lockedMargin"
                  :precision="2"
                  display-style="currency"
                  currency-display="symbol"
                  :grouping="true"
                />
              </span>
            </div>

            <div class="account-overview__row">
              <span class="account-overview__label">{{ $t('modules.accountOverview.header.realisedPnl') }}</span>
              <span class="account-overview__value">
                <ot-number-display
                  :number="accountData.realisedPnL"
                  :precision="2"
                  display-style="currency"
                  currency-display="symbol"
                  :grouping="true"
                  :class="{
                    'account-overview__profit--positive': accountData.realisedPnL > 0,
                    'account-overview__profit--negative': accountData.realisedPnL < 0,
                  }"
                />
              </span>
            </div>
          </section>
        </div>
      </div>
    </template>
  </ot-module-container>
</template>

<style lang="scss">
.account-overview {
  &__margin-ratio {
    &--active {
      color: rgb(var(--v-theme-success));
    }

    &--derisk {
      color: rgb(var(--v-theme-warning));
    }

    &--liquidation {
      color: rgb(var(--v-theme-error));
    }
  }
}
</style>

<style lang="scss" scoped>
/* stylelint-disable */
.account-overview {
  padding: 8px;
  color: rgb(var(--v-theme-text-primary));

  &__container {
    display: flex;
    flex-direction: column;
  }

  &__section {
    border-bottom: 1px solid rgba(var(--v-theme-border-primary), 0.12);

    &:last-child {
      border-bottom: none;
    }
  }

  &__title {
    font-size: var(--font-size-md);
    margin-bottom: 4px;
  }

  &__subtitle {
    color: rgb(var(--v-theme-text-secondary));
    font-size: var(--font-size-sm);
    font-weight: normal;
  }

  &__row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2px;
  }

  &__label {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: var(--font-size-sm);
    color: rgb(var(--v-theme-text-secondary));
  }

  &__value {
    font-family: var(--font-family-mono);
    font-size: var(--font-size-sm);
  }

  &__info-icon {
    height: 12px;
    width: 12px;
  }

  :deep(.account-overview__margin-ratio-lozenge) {
    font-weight: 600;
  }

  :deep(.account-overview__margin-ratio-lozenge--derisk) {
    border-color: rgb(var(--v-theme-warning));
    color: rgb(var(--v-theme-warning));
  }

  :deep(.account-overview__margin-ratio-lozenge--liquidation) {
    border-color: rgb(var(--v-theme-error));
    color: rgb(var(--v-theme-error));
  }

  &__profit {
    &--positive {
      color: rgb(var(--v-theme-success));
    }

    &--negative {
      color: rgb(var(--v-theme-error));
    }
  }
}
</style>
